// Shaped skeleton placeholders for the highest-traffic surfaces.
//
// The base `<Skeleton>` (ui/skeleton.tsx) is a generic animated div.
// These wrappers compose it into the exact silhouettes of the
// components they stand in for — ticket rows, message bubbles, note
// cards, activity items. The point is to keep the layout stable
// across the loading → hydrated transition so the page doesn't jump
// or flash blank, which is what makes the app feel "fast" even when
// the network isn't.
//
// Sizing rules of thumb (matched to the real components):
//   - TicketRow:        avatar 28px + subject line 14px + sender chip + status pill
//   - Message bubble:   variable width 60–80%, padding p-3, alternating sides
//   - Note:             amber-tinted card 80px tall with author chip + body
//   - Activity item:    icon dot + 2 lines (label + timestamp)
//
// All skeletons reuse the same `animate-pulse` from `<Skeleton>` so
// the page has a single rhythm rather than competing pulse cycles.

import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Render N skeletons of the same shape. Convenience for lists. */
export function SkeletonList({
  count,
  className,
  children,
}: {
  count: number;
  className?: string;
  children: (i: number) => React.ReactNode;
}) {
  return (
    <div className={className}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>{children(i)}</div>
      ))}
    </div>
  );
}

// ── Ticket row ───────────────────────────────────────────────────────────────
// Matches `TicketCard.tsx → TicketRow` — assignee avatar on the left,
// stacked subject + sender line, status pill + SLA chip + timestamp
// on the right. Width-fluid so it fits both dashboard wide column
// and right-rail narrow column.
export function TicketRowSkeleton() {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border/60 bg-background px-3 py-2.5">
      <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="h-2.5 w-1/3" />
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Skeleton className="h-4 w-12 rounded-full" />
        <Skeleton className="h-4 w-16 rounded-full" />
      </div>
    </div>
  );
}

export function TicketRowSkeletonList({ count = 6 }: { count?: number }) {
  return (
    <SkeletonList count={count} className="flex flex-col gap-1.5">
      {() => <TicketRowSkeleton />}
    </SkeletonList>
  );
}

// ── Message bubble ───────────────────────────────────────────────────────────
// Alternates inbound (left) / outbound (right) to match the real
// thread. Widths vary so the placeholder doesn't read as a perfect
// grid (which would feel artificial).
export function MessageBubbleSkeleton({ outbound = false }: { outbound?: boolean }) {
  // Pseudo-random width per bubble, deterministic so re-renders don't
  // shimmy. Cycle through a small set.
  return (
    <div className={cn('flex w-full', outbound ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'flex w-full max-w-[80%] flex-col gap-2 rounded-lg border border-border/60 p-3',
          outbound ? 'bg-primary/5' : 'bg-muted/40',
        )}
      >
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded-full" />
          <Skeleton className="h-2.5 w-20" />
        </div>
        <div className="space-y-1.5">
          <Skeleton className="h-2.5 w-full" />
          <Skeleton className="h-2.5 w-11/12" />
          <Skeleton className="h-2.5 w-3/5" />
        </div>
      </div>
    </div>
  );
}

export function MessageThreadSkeleton({ count = 4 }: { count?: number }) {
  return (
    <SkeletonList count={count} className="flex flex-col gap-3">
      {(i) => <MessageBubbleSkeleton outbound={i % 3 === 1} />}
    </SkeletonList>
  );
}

// ── Note row ─────────────────────────────────────────────────────────────────
// Matches `NotesPanel.tsx → NoteRow` — amber-tinted card with author
// avatar/name header + body lines. Slightly narrower than message
// bubbles to read as the side-panel item it is.
export function NoteRowSkeleton() {
  return (
    <div className="rounded-md border border-tint-warning-ring/60 bg-tint-warning/40 px-2.5 py-2">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Skeleton className="h-4 w-4 rounded-full bg-tint-warning-ring/50" />
        <Skeleton className="h-2.5 w-20 bg-tint-warning-ring/50" />
        <Skeleton className="h-2.5 w-12 bg-tint-warning-ring/40" />
      </div>
      <div className="space-y-1.5">
        <Skeleton className="h-2.5 w-full bg-tint-warning-ring/40" />
        <Skeleton className="h-2.5 w-4/5 bg-tint-warning-ring/40" />
      </div>
    </div>
  );
}

export function NotesPanelSkeleton({ count = 2 }: { count?: number }) {
  return (
    <SkeletonList count={count} className="grid gap-2">
      {() => <NoteRowSkeleton />}
    </SkeletonList>
  );
}

// ── Activity item ────────────────────────────────────────────────────────────
// Matches `TicketActivitySidebar.tsx` rows — coloured icon dot on
// the left, two stacked text lines on the right (event label + time).
export function ActivityItemSkeleton() {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Skeleton className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-1">
        <Skeleton className="h-2.5 w-3/4" />
        <Skeleton className="h-2 w-1/4" />
      </div>
    </div>
  );
}

export function ActivityListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <SkeletonList count={count} className="flex flex-col">
      {() => <ActivityItemSkeleton />}
    </SkeletonList>
  );
}

// ── Ticket detail header ─────────────────────────────────────────────────────
// The big subject + status + SLA cluster at the top of the ticket
// detail page. Approximates the line heights of `route/ticket-detail.tsx`
// header block so the transition to hydrated content doesn't jump.
export function TicketDetailHeaderSkeleton() {
  return (
    <div className="border-b border-border/70 pb-4">
      <div className="flex items-start justify-between gap-4">
        <Skeleton className="h-7 w-2/5" />
        <div className="flex shrink-0 items-center gap-1.5">
          <Skeleton className="h-5 w-10 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Skeleton className="h-5 w-32 rounded-full" />
        <Skeleton className="h-5 w-32 rounded-full" />
      </div>
    </div>
  );
}

// ── Row card (admin pages — team, contacts, journeys, kb, templates) ─────────
// The list-of-cards pattern used on every admin route: a horizontal card with
// an avatar/icon hint on the left, two text lines in the middle, a small
// affordance cluster on the right. Matches the post-redesign Card primitive
// (rounded-md, soft border, no shadow).
export function RowCardSkeleton() {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border/70 bg-background p-3">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-2.5 w-1/2" />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Skeleton className="h-6 w-14 rounded" />
        <Skeleton className="h-6 w-6 rounded" />
      </div>
    </div>
  );
}

export function RowCardSkeletonList({ count = 5 }: { count?: number }) {
  return (
    <SkeletonList count={count} className="grid gap-2">
      {() => <RowCardSkeleton />}
    </SkeletonList>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────
// Shared "nothing-here-yet" card. Lives next to skeletons because they're two
// faces of the same coin — both stand in for missing content and both should
// feel like part of the page rather than a glitch. Icon sits in a tinted
// rounded square (matches the activity-row icon pills + notification accents).
// Use this everywhere we used to render a bare "<p>No X yet</p>".
//
// Sizing is intentionally compact (py-6, not py-12) — most empty states in
// this app live inside side-panel cards, not full-page sections. Pass
// `className="py-10"` if you want it taller for a full route.
export function EmptyState({
  icon,
  title,
  subtitle,
  action,
  className,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-md px-3 py-6 text-center',
        className,
      )}
    >
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground ring-1 ring-inset ring-border/60">
        {icon}
      </span>
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium text-foreground/90">{title}</p>
        {subtitle ? (
          <p className="max-w-[28ch] text-2xs leading-snug text-muted-foreground">
            {subtitle}
          </p>
        ) : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

// ── Compact card (right-rail, contact panels, etc.) ──────────────────────────
// Generic placeholder for the right-rail accessory panels — a
// rectangle with a title strip + a couple of body lines. Useful as a
// catch-all where a more specific skeleton would be overkill.
export function PanelSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="rounded-md border border-border/70 bg-background p-3">
      <Skeleton className="mb-2 h-3 w-1/3" />
      <SkeletonList count={rows} className="space-y-1.5">
        {(i) => <Skeleton className={cn('h-2.5', i === rows - 1 ? 'w-2/3' : 'w-full')} />}
      </SkeletonList>
    </div>
  );
}
